import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, type SocialProvider } from '@/hooks/useAuth';
import TermsAgreement from './components/TermsAgreement';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function RegisterPage() {
  const { signUp, signInWithOAuth, user, recordAgreements } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [step, setStep] = useState<'form' | 'terms'>('form');
  const [emailSent, setEmailSent] = useState(false);

  usePageMeta({
    title: '회원가입 | LottoLab — 무료 로또 번호 생성·통계 서비스',
    description:
      'LottoLab에 가입하고 무료 로또 번호 생성, 당첨번호 통계 분석, 번호 저장 및 당첨 확인 기능을 모두 이용하세요. 카카오·Google 소셜 로그인으로 간편하게 시작할 수 있습니다.',
    keywords:
      '로또 회원가입, LottoLab 가입, 무료 로또 서비스, 로또 번호 생성기, 로또 통계 분석, 로또랩',
    canonical: 'https://lottolab.app/register',
  });

  if (user) {
    navigate('/', { replace: true });
    return null;
  }

  const handleGoToTerms = () => {
    setError('');
    if (!email.trim()) {
      setError('이메일을 입력해주세요.');
      return;
    }
    if (!password.trim()) {
      setError('비밀번호를 입력해주세요.');
      return;
    }
    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setStep('terms');
    setShowTerms(true);
  };

  const handleTermsAgree = () => {
    setTermsAgreed(true);
    setShowTerms(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!termsAgreed) {
      setError('약관에 동의해주세요.');
      return;
    }

    setLoading(true);
    const result = await signUp(email, password, nickname || '사용자');

    if (result.error) {
      setLoading(false);
      setError(result.error);
    } else if (result.user) {
      if (result.hasSession) {
        // 이메일 확인이 꺼져 있어 즉시 세션이 있는 경우 → 원자적으로 약관 동의 저장
        const agreementResult = await recordAgreements();
        if (agreementResult.error) {
          setLoading(false);
          setError('약관 동의 저장에 실패했습니다. 다시 시도해주세요.');
          return;
        }
        navigate('/', { replace: true });
      } else {
        // 이메일 확인 필요 → 동의는 확인 완료 후 TermsGate에서 원자적으로 저장
        setLoading(false);
        setEmailSent(true);
      }
    } else {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    setError('');
    setSocialLoading(provider);
    const result = await signInWithOAuth(provider);
    if (result.error) {
      setError(result.error);
      setSocialLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center">
              <i className="ri-flask-line text-white text-sm"></i>
            </div>
            <span className="font-heading text-2xl font-bold"><span className="text-primary-700">Lotto</span><span className="text-secondary-500">Lab</span></span>
          </Link>
        </div>

        {/* Terms modal */}
        {showTerms && (
          <TermsAgreement
            onClose={() => setShowTerms(false)}
            onAgree={handleTermsAgree}
          />
        )}

        {/* Card */}
        <div className="bg-background-50 rounded-2xl p-8 border border-background-200">
          <h1 className="font-heading text-2xl text-foreground-800 mb-1">회원가입</h1>
          <p className="text-foreground-400 text-sm mb-5">LottoLab과 함께 행운을 실험하세요!</p>

          {emailSent && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-mail-send-line text-2xl text-secondary-600"></i>
              </div>
              <h2 className="font-heading text-xl text-foreground-800 mb-2">이메일 확인이 필요합니다</h2>
              <p className="text-foreground-500 text-sm mb-6">
                <strong className="text-foreground-700">{email}</strong>로 인증 링크를 보냈습니다.
                이메일을 확인한 뒤 로그인해주세요.
              </p>
              <Link to="/login" className="inline-block px-6 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm cursor-pointer whitespace-nowrap">
                로그인으로 돌아가기
              </Link>
            </div>
          )}

          {!emailSent && (
            <>
          {error && (
            <div className="mb-5 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
              <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
              <p className="text-accent-700 text-sm">{error}</p>
            </div>
          )}

          {/* Social Signup Buttons */}
          <div className="space-y-2.5 mb-5">
            <button
              onClick={() => handleSocialLogin('kakao')}
              disabled={socialLoading !== null}
              className="w-full py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#FEE500', color: '#191919' }}
            >
              {socialLoading === 'kakao' ? (
                <span className="w-4 h-4 border-2 border-[#191919]/30 border-t-[#191919] rounded-full animate-spin"></span>
              ) : (
                <i className="ri-kakao-talk-fill text-lg"></i>
              )}
              카카오로 시작하기
            </button>

            <button
              onClick={() => handleSocialLogin('google')}
              disabled={socialLoading !== null}
              className="w-full py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap border border-background-300 text-foreground-700 hover:bg-background-50 bg-background-50"
            >
              {socialLoading === 'google' ? (
                <span className="w-4 h-4 border-2 border-foreground-300 border-t-foreground-600 rounded-full animate-spin"></span>
              ) : (
                <i className="ri-google-fill text-lg"></i>
              )}
              Google로 시작하기
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-background-200"></div>
            <span className="text-xs text-foreground-300 whitespace-nowrap">또는 이메일로 가입</span>
            <div className="flex-1 h-px bg-background-200"></div>
          </div>

          {step === 'form' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  이메일
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="hello@example.com"
                  autoComplete="email"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="nickname" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  닉네임 <span className="text-foreground-300">(선택)</span>
                </label>
                <input
                  id="nickname"
                  type="text"
                  name="nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="별명을 입력해주세요"
                  maxLength={20}
                  autoComplete="username"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  비밀번호
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6자 이상 입력해주세요"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="passwordConfirm" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  비밀번호 확인
                </label>
                <input
                  id="passwordConfirm"
                  type="password"
                  name="passwordConfirm"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="비밀번호를 다시 입력해주세요"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <button
                onClick={handleGoToTerms}
                disabled={socialLoading !== null}
                className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                다음
              </button>
            </div>
          )}

          {step === 'terms' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-4 rounded-xl bg-background-50 border border-background-200">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setTermsAgreed(!termsAgreed)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                      termsAgreed ? 'bg-primary-500 border-primary-500' : 'border-background-300'
                    }`}
                  >
                    {termsAgreed && <i className="ri-check-line text-white text-xs"></i>}
                  </button>
                  <div className="flex-1">
                    <p className="text-sm text-foreground-700">
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        className="text-primary-600 hover:text-primary-700 font-medium underline cursor-pointer"
                      >
                        서비스 이용약관
                      </button>
                      과{' '}
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        className="text-primary-600 hover:text-primary-700 font-medium underline cursor-pointer"
                      >
                        개인정보 처리방침
                      </button>
                      에 동의합니다.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !termsAgreed}
                className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    가입 중...
                  </span>
                ) : (
                  '회원가입 완료'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('form')}
                className="w-full py-2.5 rounded-xl border border-background-300 text-foreground-600 font-medium text-sm hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                이전
              </button>
            </form>
          )}

          <div className="mt-6 pt-5 border-t border-background-200 text-center">
            <p className="text-foreground-400 text-sm">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                로그인
              </Link>
            </p>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}